/**
 * Phase A: reach the wiper API through Shopify's app proxy first, the direct
 * app domain second.
 *
 * Why: shoppers on some networks (Comcast/Xfinity, LogRocket "Network Error
 * 0", 2026-10-19) cannot reach app.kimbladeapp.com at all, but kimblade.com
 * always works. The proxy path (/apps/wiper/*) is served from kimblade.com and
 * forwarded by Shopify to the app, so the browser never contacts the app
 * domain.
 *
 * How: a jQuery ajax transport. Every $.ajax whose URL starts with the App URL
 * (all 13 theme call sites do) is sent to the proxy first; if that fails the
 * same request is replayed against the direct URL, and only the final outcome
 * reaches the caller's success/error callbacks. A prefilter cannot do this:
 * jQuery attaches the caller's error callback before prefilters run.
 *
 * Fall back when the proxy attempt: failed to connect, timed out, returned 5xx
 * or 401 (signature / proxy misconfiguration), or returned something that is
 * not JSON (a Shopify error page, a Liquid 404). A JSON 4xx such as
 * find-wipers' 404 "no matching wipers" is a real answer and is not retried.
 *
 * Config comes from layout/theme.liquid (window.WiperApiConfig); `mode` is the
 * rollback switch: 'direct' disables all of this and behaves like before.
 */
(function (window, $) {
  if (!$ || !$.ajaxTransport || window.WiperApi) return;

  var cfg = window.WiperApiConfig || {};
  if (!cfg.appUrl) return;

  var DIRECT = String(cfg.appUrl).replace(/\/+$/, '');
  var PROXY = String(cfg.proxyPath || '/apps/wiper').replace(/\/+$/, '');
  var MODE = cfg.mode === 'direct' || cfg.mode === 'direct_first' ? cfg.mode : 'proxy_first';
  // Provisional until Shopify's proxy timeout is measured (Phase A, step A2);
  // set from the theme setting so it can be tuned without a deploy.
  var PROXY_TIMEOUT_MS = Number(cfg.proxyTimeoutMs) || 8000;

  function isAppUrl(url) {
    return typeof url === 'string' && url.indexOf(DIRECT + '/') === 0;
  }

  function toProxy(url) {
    return PROXY + url.slice(DIRECT.length);
  }

  function pathOf(url) {
    return url.slice(DIRECT.length).split('?')[0];
  }

  function track(url, from, reason) {
    try {
      if (window.LogRocket && typeof window.LogRocket.track === 'function') {
        window.LogRocket.track('wiper_api_fallback', { path: pathOf(url), from: from, reason: reason });
      }
    } catch (e) { /* telemetry must never break the finder */ }
    try {
      console.warn('[wiper-api] ' + from + ' failed (' + reason + ') for ' + pathOf(url) + ', trying the other route');
    } catch (e) { /* no console */ }
  }

  /** Resolves (never rejects) with what came back, or why nothing did. */
  function run(url, method, body, headers, timeoutMs, state) {
    return new Promise(function (resolve) {
      var controller = window.AbortController ? new window.AbortController() : null;
      var timedOut = false;
      var timer = null;

      state.abort = function () {
        if (controller) controller.abort();
      };

      if (timeoutMs > 0) {
        timer = setTimeout(function () {
          timedOut = true;
          if (controller) controller.abort();
          resolve({ error: 'timeout', status: 0, statusText: 'timeout' });
        }, timeoutMs);
      }

      var init = { method: method, headers: headers };
      if (body !== undefined && body !== null) init.body = body;
      if (controller) init.signal = controller.signal;

      window.fetch(url, init)
        .then(function (response) {
          return response.text().then(function (text) {
            if (timer) clearTimeout(timer);
            if (timedOut) return;
            var contentType = response.headers.get('Content-Type') || '';
            resolve({
              error: null,
              status: response.status,
              statusText: response.statusText || '',
              text: text,
              contentType: contentType,
              headersString: 'Content-Type: ' + contentType + '\r\n',
            });
          });
        })
        .catch(function () {
          if (timer) clearTimeout(timer);
          if (timedOut) return;
          resolve({ error: 'network', status: 0, statusText: 'error' });
        });
    });
  }

  function shouldFallBack(result) {
    if (result.error) return result.error;
    if (result.status >= 500) return 'http_' + result.status;
    if (result.status === 401) return 'http_401';
    if (!/json/i.test(result.contentType)) return 'not_json';
    return null;
  }

  $.ajaxTransport('+*', function (options) {
    if (MODE === 'direct' || !window.fetch || !isAppUrl(options.url)) return;

    var state = { aborted: false, abort: null };

    return {
      send: function (jqHeaders, complete) {
        var method = (options.type || 'GET').toUpperCase();
        var body = options.hasContent ? options.data : null;
        var order = MODE === 'direct_first' ? ['direct', 'proxy'] : ['proxy', 'direct'];

        function attempt(i) {
          var route = order[i];
          var last = i === order.length - 1;
          var url = route === 'proxy' ? toProxy(options.url) : options.url;
          var timeoutMs = route === 'proxy' ? PROXY_TIMEOUT_MS : 0;

          run(url, method, body, jqHeaders, timeoutMs, state).then(function (result) {
            if (state.aborted) return;

            var reason = shouldFallBack(result);
            if (reason && !last) {
              track(options.url, route, reason);
              attempt(i + 1);
              return;
            }

            if (result.error) {
              complete(0, result.statusText);
            } else {
              complete(result.status, result.statusText, { text: result.text }, result.headersString);
            }
          });
        }

        attempt(0);
      },

      abort: function () {
        state.aborted = true;
        if (state.abort) state.abort();
      },
    };
  });

  window.WiperApi = { mode: MODE, isAppUrl: isAppUrl, toProxy: toProxy };
})(window, window.jQuery);
